/* eslint-disable complexity */
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import { Weekday } from '@chancenwerk/enums/enums.js';
import type { Course } from '@chancenwerk/shared/courses/Course.js';
import { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import { Mitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/Mitarbeiter.js';
import { formatDayAvailable, Student } from '@chancenwerk/shared/master-data-management/users/entities/Student.js';
import type { StudentsReport } from '@chancenwerk/shared/StudentsReport.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

/**
 * @customElement board-component-user-card
 */
@customElement('board-component-user-card')
export class BoardComponentUserCard extends ChemistryLitElement {
  @property({ attribute: false }) public oldAssignments = new RXController<Course[]>(this, []);
  @property({ attribute: false }) public studentsReport: StudentsReport | null = null;
  @property({ attribute: false }) public showCourse = false;
  @property({ attribute: false }) public showChoosenCourses = false;
  @property({ attribute: false }) public warning: Maybe<string> = null;
  /**
   * Severity of `warning`. 'error' (rot) is reserved for people who are gone - ausgetreten/gekuendigt
   * or no longer active. 'info' (blau) flags data that needs a look: a course day the student did not
   * pick, or no Durchfuehrungstage in the profile at all.
   */
  @property({ attribute: false }) public warningTone: 'error' | 'info' = 'error';
  @property({ attribute: false }) public assignedCourses: Course[] = [];
  /** All day-courses managed by the surrounding (aggregated) card; used to show per-day coverage. */
  @property({ attribute: false }) public allCourses: Course[] = [];

  @property({ attribute: false }) public user = new ChancenwerkUser({});

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          display: 'block',
          '-webkit-user-select': 'none',
          '-ms-user-select': 'none',
          'user-select': 'none',
          borderRadius: '8px !important',
          padding: '9px',
          background: this._backgroundColor(),
          maxWidth: '100%',
        },
      },
    ];
  }

  private _backgroundColor() {
    if (this.user instanceof Student || this.user instanceof Mitarbeiter) {
      if (this.user.has('guestAccount') && this.user.guestAccount) {
        return '#ffd166';
      }
    }

    if (this._inactive().state) {
      return this.colors.accent;
    }

    if (this.warning) {
      // `tone.info` is a project tone, so it reaches us through the Tones index signature and is
      // typed as optional - fall back to the error tone rather than losing the background entirely.
      const tone = this.warningTone === 'info' ? this.colors.tone.info : this.colors.tone.error;
      return (tone ?? this.colors.tone.error).shade(300);
    }

    return 'white';
  }

  private _inactive(): { state: boolean; reason: string } {
    if (this.user._class !== Student._class) {
      return { state: false, reason: '' };
    }

    if (this.studentsReport?.reports) {
      const report = this.studentsReport.reports.find(r => r.userID === this.user._id);
      if (report && report.present === 0 && (report.missing ?? 0) >= 5 && report.excused === 0) {
        return { state: true, reason: 'War im letzten Monat nicht anwesend' };
      }
    }

    return { state: !!(this.user as Student).inactive, reason: 'Inaktivität im Raum gesetzt' };
  }

  private _preferedSubjects() {
    if (!this._hasPreferedSubjects()) {
      return nothing;
    }

    const student = this.user as Student;
    const subjects = [student.preferedFirstSubject, student.preferedSecondSubject, student.preferedThirdSubject].filter(
      s => s !== null && s !== undefined && s !== '',
    );

    if (!subjects.length) return nothing;

    return html`<cr-flex space="xs">
      <cr-text ${css({ fontSize: '13px' })}>${subjects.length > 1 ? 'Wunschfächer' : 'Wunschfach'}</cr-text>
      <cr-flex space="xs" horizontal ${css({ flexWrap: 'wrap' })}
        >${subjects.map(
          subject =>
            html`<cr-chip ${css({ background: this.colors.primary.shade(50), borderRadius: '5px' })}
              ><cr-text ${css({ fontSize: '12px', padding: '6px' })}>${subject}</cr-text></cr-chip
            >`,
        )}</cr-flex
      >
    </cr-flex>`;
  }

  private _hasPreferedSubjects() {
    if (!(this.user instanceof Student)) {
      return false;
    }

    if (
      !this.user.has('preferedFirstSubject') ||
      !this.user.has('preferedSecondSubject') ||
      !this.user.has('preferedThirdSubject')
    ) {
      return false;
    }

    return true;
  }

  private _school() {
    if (!(this.user instanceof Student)) {
      return nothing;
    }

    if (!this.user.has('schoolInformation')) {
      return nothing;
    }

    const kpf = this.user.kpfs?.[0];
    if (!kpf?.school?.name) {
      return nothing;
    }

    return html`<cr-text>${kpf.school.name}</cr-text>`;
  }

  private _guest() {
    if (!(this.user instanceof Student || this.user instanceof Mitarbeiter)) {
      return nothing;
    }

    if (!this.user.has('guestAccount') || !this.user.guestAccount) {
      return nothing;
    }

    return html`<cr-text bold>Gast</cr-text>`;
  }

  private _graduationInfo() {
    if (!(this.user instanceof Student) || !this.user.has('graduationInformation')) {
      return nothing;
    }

    const info = this.user.graduationInformation;
    if (!info) {
      return nothing;
    }

    return html`<cr-text>${info.type}</cr-text>
      <cr-flex space="xs" horizontal ${css({ flexWrap: 'wrap' })}>
        ${[
          info.firstSubjectGraduation,
          info.secondSubjectGraduation,
          info.thirdSubjectGraduation,
          info.openSubjectGraduation,
        ]
          .filter(Boolean)
          .map(
            s =>
              html`<cr-chip ${css({ background: this.colors.primary.shade(50), borderRadius: '5px' })}>
                <cr-text ${css({ fontSize: '12px', padding: '6px' })}>${s}</cr-text>
              </cr-chip>`,
          )}
      </cr-flex>

      <cr-flex space="xs" horizontal ${css({ flexWrap: 'wrap' })}>
        ${info.daysAvailable.filter(Boolean).map(
          day =>
            html`<cr-chip ${css({ background: this.colors.primary.shade(50), borderRadius: '5px' })}>
              <cr-text ${css({ fontSize: '12px', padding: '6px' })}> ${formatDayAvailable(day)} </cr-text>
            </cr-chip>`,
        )}
      </cr-flex>`;
  }

  private _courseDays() {
    // choosen days from the Chancenraum profile take precedence over current course assignments
    if (this.showChoosenCourses && this.user instanceof Student) {
      const days = this.user.lernfoerderungDuerchfuehrungsTagChoosen ?? [];
      if (!days.length) return nothing;
      return html`
        <cr-flex space="sm" horizontal wrap>
          ${repeat(days, t => html` <cr-text bold> ${t.wochentag} </cr-text> `)}
        </cr-flex>
      `;
    }

    if (!this.assignedCourses.length) return nothing;
    return html`
      <cr-flex space="sm" horizontal wrap>
        ${repeat(this.assignedCourses, c => html` <cr-text bold> ${c.weekday} </cr-text> `)}
      </cr-flex>
    `;
  }

  /**
   * In a multi-day (aggregated) course card, render one chip per managed day: the days the student is
   * assigned to are filled, the remaining days are shown muted. This makes a student who only attends
   * some of the days visible at a glance. Renders nothing for single-day cards or for a student who is
   * already on every day.
   */
  private _dayCoverage() {
    if (this.allCourses.length < 2) return nothing;

    const assignedIDs = new Set((this.oldAssignments.value ?? []).map(c => c._id));
    if (this.allCourses.every(c => assignedIDs.has(c._id))) return nothing;

    const order = Object.values(Weekday);
    const days = [...this.allCourses].sort(
      (a, b) => order.indexOf(a.weekday ?? Weekday.MONDAY) - order.indexOf(b.weekday ?? Weekday.MONDAY),
    );
    const weekdayCounts = new Map<string, number>();
    for (const c of days) weekdayCounts.set(c.weekday ?? '?', (weekdayCounts.get(c.weekday ?? '?') ?? 0) + 1);

    return html`
      <cr-flex space="xs" horizontal wrap crossaxis-center>
        ${repeat(
          days,
          c => c._id,
          c => {
            const assigned = assignedIDs.has(c._id);
            const label =
              (weekdayCounts.get(c.weekday ?? '?') ?? 0) > 1 && c.startTime
                ? `${c.weekday ?? '?'} ${c.startTime.toFormat('HH:mm')}`
                : (c.weekday ?? '?');
            return html`<cr-chip
              ${css({
                background: assigned ? this.colors.primary.shade(100) : 'transparent',
                border: assigned ? 'none' : `1px solid ${this.colors.neutral.shade(200)}`,
                borderRadius: '5px',
                opacity: assigned ? '1' : '0.55',
              })}
            >
              <cr-text ${css({ fontSize: '12px', padding: '4px 6px' })} ?bold=${assigned}> ${label} </cr-text>
            </cr-chip>`;
          },
        )}
      </cr-flex>
    `;
  }

  override render() {
    const inactive = this._inactive();

    return html`
      <cr-flex space="sm">
        <cr-flex space="sm" horizontal space-between crossaxis-center>
          ${inactive.state || this.warning
            ? html`<cr-tooltip><cr-text>${this.warning ?? inactive.reason}</cr-text></cr-tooltip>`
            : nothing}

          <cr-flex crossaxis-center horizontal ${css({ maxWidth: '80%' })}>
            <cr-icon ${css({ color: this.colors.neutral })}>drag_indicator</cr-icon>
            <cr-text
              ellipsis
              ?bold=${this._hasPreferedSubjects()}
              .value=${`${
                this.user.vorname && this.user.vorname ? `${this.user.nachname}, ${this.user.vorname}` : this.user.email
              } `}
            >
            </cr-text>
          </cr-flex>

          <cr-flex horizontal crossaxis-center space="xs">
            <cr-text> ${this.user._class === Student._class ? `${(this.user as Student).schoolClass}` : ''} </cr-text>
            <cr-icon
              ${acss({ cursor: 'pointer', '&:hover': { opacity: 0.5 } })}
              @click=${() => {
                window.open(
                  `${projectConfig.get('RAUM_URL')}edit/person/${
                    this.user._class === Student._class ? 'student' : 'employee'
                  }/${this.user._id}`,
                  '_blank',
                );
              }}
            >
              link
              <cr-tooltip><cr-text>Im CHANCENraum öffnen</cr-text></cr-tooltip>
            </cr-icon>
          </cr-flex>

          ${this.showCourse && this.allCourses.length < 2
            ? html`
                <cr-flex space="sm">
                  ${repeat(this.oldAssignments.value, c => html` <cr-text bold> ${c.weekday} </cr-text> `)}
                </cr-flex>
              `
            : nothing}
        </cr-flex>
        ${this._dayCoverage()} ${this._courseDays()} ${this._guest()} ${this._preferedSubjects()} ${this._school()}
        ${this._graduationInfo()}
      </cr-flex>
    `;
  }
}
